import VadImage from "vadimages-nextjs-image-optimizer";
import {EVENT_COPY, EventStage, STAGE_COPY} from "../content";
import {Blobs} from "./Blobs";
import Lines from "./Lines";
import css from "../mjbc-2026.module.scss";

const IMG = '/images/mjbc-2026';

interface BriefingProps {
    stage: EventStage;
    countdown: string;
    /** Save the date: a calendar link. Formal invite: opens the crew manifest. */
    action: {kind: 'link'; href: string} | {kind: 'button'; onClick: () => void};
}

/**
 * The invite itself — frames 8-9 of the teaser, 9-10 of the formal invite.
 *
 * The two stages share this layout exactly: a badge column, a 70px gutter and a
 * detail column. Only the copy, the column widths and the CTA differ, so they
 * are one component keyed on `stage` rather than two near-identical ones.
 */
export default function Briefing({stage, countdown, action}: BriefingProps) {
    const copy = STAGE_COPY[stage];

    /*
     * The two stages part company inside the button as well as around it. The
     * teaser marks a calendar and draws one; the invite opens the crew manifest
     * and draws the arrow, wider and at its own size. The blob rollover behind
     * the label is shared, so the contents are built once and wrapped twice —
     * the teaser in a link out to Google Calendar, the invite in a button.
     */
    const label = (
        <>
            <Blobs/>
            <span><Lines of={copy.primaryCta}/></span>
            {stage === 'formal-invite' ? (
                <VadImage
                    src={`${IMG}/icon-arrow-right.svg`}
                    alt=""
                    width={40}
                    height={35}
                    className={css.ctaArrowSmall}
                />
            ) : (
                <VadImage src={`${IMG}/icon-calendar.svg`} alt="" width={23} height={23}/>
            )}
        </>
    );

    return (
        <section className={css.briefing} data-stage={stage}>
            <div className={css.briefingArt}>
                <VadImage
                    src={`${IMG}/ignition-sequence-badge.png`}
                    alt="Ignition Sequence"
                    width={753}
                    height={815}
                />
            </div>

            <div className={css.briefingDetail}>
                <div className={css.tagline}>
                    <span className={css.rule} aria-hidden="true"/>
                    <p>
                        <span className={css.taglineTop}>{copy.tagline[0]}</span><br/>
                        <span className={css.taglineBottom}>{copy.tagline[1]}</span>
                    </p>
                    <span className={css.rule} aria-hidden="true"/>
                </div>

                <div className={css.boardingBlock}>
                    <p className={css.boarding}>{EVENT_COPY.reportForBoarding}</p>

                    <div className={css.dateRow}>
                        {/* Stacked 12 / 02 / 26. The teaser's mobile frame runs
                            them together as "12.02.26" instead — the separators
                            are drawn in CSS so the markup stays one list of
                            numerals rather than a second copy of the date. */}
                        <p className={css.dateStack}>
                            {EVENT_COPY.dateStack.map(part => <span key={part}>{part}</span>)}
                        </p>
                        <div className={css.dateMeta}>
                            <p>{copy.location[0]}<br/>{copy.location[1]}</p>
                            <span className={css.ruleShort} aria-hidden="true"/>
                            <p><Lines of={copy.coordinates}/></p>
                        </div>
                    </div>
                </div>

                <span className={css.rule} aria-hidden="true"/>

                <p className={css.tMinus}>
                    <span className={css.tMinusLabel}>{EVENT_COPY.tMinus}</span>
                    <time className={css.tMinusValue}>{countdown}</time>
                </p>

                {action.kind === 'link' ? (
                    <a
                        className={css.calendarButton}
                        href={action.href}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {label}
                    </a>
                ) : (
                    <button type="button" className={css.calendarButton} onClick={action.onClick}>
                        {label}
                    </button>
                )}
            </div>
        </section>
    );
}
